import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Send, Loader2, FileText, PenLine, CheckCircle2, ShieldCheck,
} from "lucide-react";
import api, { clearToken, getToken, formatApiError } from "@/lib/api";
import { PortalHeader, StatusBadge, STATUS_COLORS } from "@/pages/AdminDashboard";
import { Field, inputProps } from "@/components/Field";

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-blush-200 bg-white p-6">
    <h3 className="font-serif text-lg text-charcoal">{title}</h3>
    <div className="mt-4">{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex flex-col gap-0.5 py-1.5 sm:flex-row sm:gap-4">
    <span className="w-44 shrink-0 text-xs font-semibold uppercase tracking-wide text-charcoal-faint">{k}</span>
    <span className="text-sm text-charcoal-light">{v || "—"}</span>
  </div>
);

const downloadFile = async (fileId, filename) => {
  try {
    const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(formatApiError(e));
  }
};

const AdminApplicant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [hire, setHire] = useState(null);
  const [audit, setAudit] = useState([]);
  const [status, setStatus] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [i9, setI9] = useState({ document_title: "", issuing_authority: "", document_number: "", expiration_date: "", first_day: "", employer_signature: "", notes: "" });
  const [i9Saving, setI9Saving] = useState(false);
  const [iv, setIv] = useState({ proposed_at: "", mode: "Phone", notes: "" });
  const [ivSending, setIvSending] = useState(false);
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    navigate("/admin");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/applications/${id}`);
      setApp(data.application);
      setStatus(data.application.status);
      if (data.application.hire_user_id) {
        const h = await api.get(`/admin/hires/${data.application.hire_user_id}`);
        setHire(h.data);
      }
      const logs = await api.get(`/admin/audit`, { params: { target: id } });
      setAudit(logs.data.logs);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    if (!getToken()) return navigate("/admin");
    api.get("/admin/applications")
      .then(({ data }) => setStatuses(data.statuses))
      .catch(() => { clearToken(); navigate("/admin"); });
    load();
  }, [id, navigate, load]);

  if (!app) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-blush-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blush-300 border-t-gold" />
      </div>
    );
  }

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/applications/${id}/status`, { status });
      toast.success(`Status updated to "${status}"`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async () => {
    setInviting(true);
    try {
      await api.post(`/admin/applications/${id}/onboarding-invite`);
      toast.success("Onboarding invitation emailed to the applicant.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const sendInterview = async () => {
    if (!iv.proposed_at) return toast.error("Pick a proposed date and time first.");
    setIvSending(true);
    try {
      await api.post(`/admin/applications/${id}/interview-invite`, iv);
      toast.success("Interview invitation emailed to the applicant.");
      setIv({ proposed_at: "", mode: "Phone", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIvSending(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setNoteSaving(true);
    try {
      await api.post(`/admin/applications/${id}/notes`, { text: note });
      setNote("");
      toast.success("Note saved.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNoteSaving(false);
    }
  };

  const saveI9 = async (e) => {
    e.preventDefault();
    setI9Saving(true);
    try {
      await api.post(`/admin/hires/${app.hire_user_id}/i9-section2`, i9);
      toast.success("Employer Section 2 saved.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setI9Saving(false);
    }
  };

  const av = app.availability || {};
  const weekSummary = Object.entries(av.week || {})
    .filter(([, v]) => v.am || v.pm)
    .map(([d, v]) => `${d.slice(0, 3)} ${[v.am && "AM", v.pm && "PM"].filter(Boolean).join("/")}`)
    .join(", ");
  const ivStatus = app.interview?.status;

  return (
    <div className="min-h-screen bg-blush-50/50">
      <PortalHeader title="Applicant Review" onLogout={logout} />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <Link to="/admin/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-gold-deep hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to all applicants
        </Link>

        <div className="mt-5 flex flex-wrap items-start justify-between gap-6">
          <div>
            <h1 className="font-serif text-3xl text-charcoal" data-testid="applicant-name">{app.legal_name}</h1>
            <p className="mt-1 text-sm text-charcoal-muted">
              Applied {app.created_at ? new Date(app.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""} &middot; {app.email} &middot; {app.phone}
            </p>
            <div className="mt-3"><StatusBadge status={app.status} /></div>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <span className="field-label">Application status</span>
              <select data-testid="applicant-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="field-input">
                {statuses.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <button onClick={saveStatus} disabled={saving || status === app.status} data-testid="applicant-status-save"
              className="btn-pink !px-5 !py-3 !text-xs disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-blush-200 bg-white p-6" data-testid="interview-section">
          <h3 className="font-serif text-lg text-charcoal">Interview</h3>
          {app.interview && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-blush-200 bg-blush-50/60 px-4 py-3 text-sm">
              <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                ivStatus === "confirmed" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : ivStatus === "change_requested" ? "border-amber-200 bg-amber-50 text-amber-700"
                : "border-blue-200 bg-blue-50 text-blue-700"}`} data-testid="interview-status-badge">
                {ivStatus === "confirmed" ? "Confirmed" : ivStatus === "change_requested" ? "Reschedule requested" : "Invitation sent"}
              </span>
              <span className="text-charcoal-light">
                {app.interview.mode} · {new Date(app.interview.proposed_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
              {app.interview.applicant_note && <span className="text-xs text-amber-700">Note: {app.interview.applicant_note}</span>}
            </div>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field label="Proposed date & time" htmlFor="iv-when">
              <input id="iv-when" type="datetime-local" value={iv.proposed_at} onChange={(e) => setIv({ ...iv, proposed_at: e.target.value })} {...inputProps("interview-datetime-input")} />
            </Field>
            <Field label="Interview mode" htmlFor="iv-mode">
              <select id="iv-mode" value={iv.mode} onChange={(e) => setIv({ ...iv, mode: e.target.value })} {...inputProps("interview-mode-select")}>
                <option>Phone</option><option>Video call</option><option>In-person</option>
              </select>
            </Field>
            <button onClick={sendInterview} disabled={ivSending} data-testid="send-interview-invite" className="btn-gold-outline !py-3 !text-xs disabled:opacity-60">
              {ivSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {app.interview ? "Send New Invitation" : "Send Interview Invitation"}
            </button>
          </div>
          <div className="mt-4">
            <Field label="Note to applicant (optional)" htmlFor="iv-notes">
              <input id="iv-notes" value={iv.notes} onChange={(e) => setIv({ ...iv, notes: e.target.value })} placeholder="e.g. We'll discuss the CHHA opening in Camden County" {...inputProps("interview-notes-input")} />
            </Field>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <Section title="Personal Information">
            <KV k="Address" v={[app.address, app.city, app.state, app.zip].filter(Boolean).join(", ")} />
            <KV k="Preferred contact" v={app.preferred_contact} />
          </Section>
          <Section title="Position & Availability">
            <KV k="Position(s)" v={(app.positions || []).join(", ") + (app.other_position ? ` (${app.other_position})` : "")} />
            <KV k="Heard about us via" v={app.hear_about} />
            <KV k="Salary desired" v={app.salary_desired} />
            <KV k="Can start" v={app.start_date} />
            <KV k="Employment type" v={(av.types || []).join(", ")} />
            <KV k="Shifts" v={(av.shifts || []).join(", ")} />
            <KV k="Weekly availability" v={weekSummary} />
            <KV k="Overtime" v={av.overtime} />
            <KV k="Days / hours" v={av.details} />
          </Section>
          <Section title="Service Area & Eligibility">
            <KV k="Willing to work in" v={app.service_areas} />
            <KV k="Reliable transportation" v={app.reliable_transportation} />
            <KV k="Driver's license" v={app.drivers_license} />
            <KV k="18 or older" v={app.is_18} />
            <KV k="Authorized to work in US" v={app.work_authorized} />
            <KV k="Proof document" v={app.proof_document} />
            <KV k="Criminal conviction" v={app.convicted} />
            {app.convicted === "Yes" && <KV k="Conviction details" v={app.conviction_details} />}
          </Section>
          <Section title="Licenses & Certifications">
            {(app.licenses || []).filter((l) => l.type).length === 0 ? <p className="text-sm text-charcoal-faint">None listed.</p> :
              app.licenses.filter((l) => l.type).map((l, i) => (
                <KV key={i} k={l.type} v={[l.number && `#${l.number}`, l.issuing_authority, l.expiration && `exp. ${l.expiration}`].filter(Boolean).join(" · ")} />
              ))}
            {(app.malpractice_policy || app.malpractice_carrier) && (
              <div className="mt-3 border-t border-blush-100 pt-3">
                <KV k="Malpractice policy" v={app.malpractice_policy} />
                <KV k="Carrier" v={app.malpractice_carrier} />
                <KV k="Carrier address" v={app.malpractice_carrier_address} />
              </div>
            )}
          </Section>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Section title="Education and Training">
            {(app.education || []).filter((e) => e.school).length === 0 ? <p className="text-sm text-charcoal-faint">None listed.</p> :
              app.education.filter((e) => e.school).map((e, i) => (
                <div key={i} className="border-b border-blush-100 py-3 last:border-none">
                  <p className="text-sm font-semibold text-charcoal">{e.level ? `${e.level} — ` : ""}{e.school}</p>
                  <p className="text-sm text-charcoal-muted">{[e.degree, e.year && `Class of ${e.year}`].filter(Boolean).join(" · ")}</p>
                </div>
              ))}
          </Section>
          <Section title="Previous Employment">
            {(app.employment || []).filter((j) => j.employer).length === 0 ? <p className="text-sm text-charcoal-faint">None listed.</p> :
              app.employment.filter((j) => j.employer).map((j, i) => (
                <div key={i} className="border-b border-blush-100 py-3 last:border-none">
                  <p className="text-sm font-semibold text-charcoal">{j.job_title || "—"} at {j.employer}</p>
                  <p className="text-xs text-charcoal-faint">{[j.from, j.to].filter(Boolean).join(" – ")}{j.supervisor ? ` · Supervisor: ${j.supervisor}` : ""}{j.employer_phone ? ` · ${j.employer_phone}` : ""}</p>
                  {(j.employer_address || j.employer_city_state_zip) && <p className="mt-1 text-xs text-charcoal-faint">{[j.employer_address, j.employer_city_state_zip].filter(Boolean).join(", ")}</p>}
                  {j.reason_leaving && <p className="mt-1 text-xs text-charcoal-faint">Reason for leaving: {j.reason_leaving}</p>}
                </div>
              ))}
          </Section>
          <Section title="Skills & Military Service">
            {app.skills ? <p className="text-sm leading-relaxed text-charcoal-light">{app.skills}</p> : <p className="text-sm text-charcoal-faint">No skills listed.</p>}
            {(app.military?.branch || app.military?.rank) && (
              <div className="mt-3 border-t border-blush-100 pt-3">
                <KV k="Branch" v={app.military.branch} />
                <KV k="Rank at discharge" v={app.military.rank} />
                <KV k="Years served" v={app.military.years} />
                <KV k="Relevant skills" v={app.military.skills} />
              </div>
            )}
          </Section>
          <Section title="References">
            {(app.references || []).filter((r) => r.name).map((r, i) => (
              <div key={i} className="border-b border-blush-100 py-3 last:border-none">
                <p className="text-sm font-semibold text-charcoal">{r.name}{r.relationship ? ` — ${r.relationship}` : ""}</p>
                <p className="text-sm text-charcoal-muted">{[r.company, r.phone, r.email].filter(Boolean).join(" · ")}</p>
              </div>
            ))}
          </Section>
          <Section title="Uploaded Documents">
            {(app.documents || []).length === 0 ? <p className="text-sm text-charcoal-faint">No documents uploaded.</p> :
              app.documents.map((d) => (
                <div key={d.file_id} className="flex items-center justify-between border-b border-blush-100 py-3 last:border-none">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-charcoal">{d.label}</p>
                      <p className="text-xs text-charcoal-faint">{d.original_filename}</p>
                    </div>
                  </div>
                  <button onClick={() => downloadFile(d.file_id, d.original_filename)} data-testid={`download-doc-${d.field}`}
                    className="flex items-center gap-1.5 rounded-full border border-gold/40 px-3.5 py-1.5 text-xs font-semibold text-gold-deep transition-colors hover:bg-gold-soft">
                    <Download className="h-3.5 w-3.5" /> Download
                  </button>
                </div>
              ))}
          </Section>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Section title="Electronic Signature">
            <p className="font-serif text-2xl italic text-charcoal">{app.signature_name}</p>
            <p className="mt-1 text-xs text-charcoal-faint">Signed on {app.signature_date} · Certification accepted</p>
          </Section>
          <Section title="Status History">
            {(app.status_history || []).slice().reverse().map((h, i) => (
              <p key={i} className="border-b border-blush-100 py-2 text-sm text-charcoal-light last:border-none">
                <span className="font-semibold">{h.status}</span>
                <span className="text-xs text-charcoal-faint"> — {new Date(h.at).toLocaleString("en-US")}{h.by ? ` by ${h.by}` : ""}</span>
              </p>
            ))}
          </Section>
        </div>

        <div className="mt-6 rounded-[2rem] border border-gold/40 bg-white p-7 sm:p-9" data-testid="onboarding-admin-section">
          <h3 className="flex items-center gap-3 font-serif text-xl text-charcoal">
            <ShieldCheck className="h-6 w-6 text-gold" strokeWidth={1.4} /> Onboarding
          </h3>
          {!hire ? (
            <div className="mt-4">
              <p className="text-sm text-charcoal-muted">
                When this applicant's status is <strong>Approved for Hire</strong>, you can email them a secure,
                single-use onboarding invitation link (expires in 7 days).
              </p>
              <button onClick={sendInvite} disabled={inviting || status !== "Approved for Hire"}
                data-testid="send-onboarding-invite"
                className="btn-pink mt-5 disabled:cursor-not-allowed disabled:opacity-50">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Onboarding Invitation
              </button>
              {status !== "Approved for Hire" && (
                <p className="mt-2 text-xs text-charcoal-faint">Set the status above to "Approved for Hire" and save to enable this.</p>
              )}
            </div>
          ) : (
            <div className="mt-5">
              <p className="text-sm text-charcoal-muted">
                Invitation sent to <strong>{hire.hire.email}</strong> — account {hire.activated ? <span className="font-semibold text-emerald-700">activated</span> : <span className="font-semibold text-amber-700">awaiting activation</span>} · {hire.required_done}/{hire.required_total} required items complete.
              </p>
              <div className="mt-4 grid gap-2.5">
                {hire.checklist.map((item) => (
                  <div key={item.key} className="flex items-center justify-between gap-3 rounded-xl border border-blush-200 bg-blush-50/40 px-4 py-3">
                    <div className="flex items-center gap-3">
                      {item.complete
                        ? <CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 shrink-0 text-emerald-600" />
                        : <span className="h-4.5 w-4.5 h-5 w-5 shrink-0 rounded-full border-2 border-blush-300" />}
                      <div>
                        <p className="text-sm font-medium text-charcoal">{item.title}{!item.required && <span className="ml-2 text-xs text-charcoal-faint">(optional)</span>}</p>
                        {item.file && <p className="text-xs text-charcoal-faint">{item.file.original_filename}</p>}
                        {item.signature_name && <p className="text-xs text-charcoal-faint">Signed by {item.signature_name}</p>}
                        {item.data && (
                          <p className="text-xs text-charcoal-faint">
                            {Object.entries(item.data).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}
                          </p>
                        )}
                        {item.attestation && item.attestation.status && (
                          <p className="text-xs text-charcoal-faint">Section 1: {item.attestation.status} · Documents: {item.attestation.document_plan} · Signed {item.attestation.signature_name}</p>
                        )}
                      </div>
                    </div>
                    {item.file && (
                      <button onClick={() => downloadFile(item.file.file_id, item.file.original_filename)} data-testid={`download-onboarding-${item.key}`}
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1.5 text-xs font-semibold text-gold-deep hover:bg-gold-soft">
                        <Download className="h-3.5 w-3.5" /> View
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl border border-gold/30 bg-gold-soft/40 p-6">
                <h4 className="font-serif text-lg text-charcoal">Form I-9 — Employer Section 2</h4>
                <p className="mt-2 text-xs leading-relaxed text-charcoal-muted">
                  Per USCIS requirements, examine the employee's <strong>original</strong> documents in person within 3 business days
                  of their first day of employment, then record the document the employee chose to present below.
                  The employee chooses which acceptable document(s) to present — never require a specific document.
                </p>
                {hire.i9_section2 ? (
                  <div className="mt-4 text-sm text-charcoal-light">
                    <KV k="Document title" v={hire.i9_section2.document_title} />
                    <KV k="Issuing authority" v={hire.i9_section2.issuing_authority} />
                    <KV k="Document number" v={hire.i9_section2.document_number} />
                    <KV k="Expiration" v={hire.i9_section2.expiration_date} />
                    <KV k="First day of employment" v={hire.i9_section2.first_day} />
                    <KV k="Employer signature" v={hire.i9_section2.employer_signature} />
                    <KV k="Completed" v={`${hire.i9_section2.completed_by} · ${new Date(hire.i9_section2.completed_at).toLocaleString("en-US")}`} />
                  </div>
                ) : (
                  <form onSubmit={saveI9} className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="i9-section2-form">
                    <Field label="Document title" htmlFor="i9-title" required>
                      <input id="i9-title" required value={i9.document_title} onChange={(e) => setI9({ ...i9, document_title: e.target.value })} placeholder="e.g. U.S. Passport / Driver's License + SS Card" {...inputProps("i9-document-title-input")} />
                    </Field>
                    <Field label="Issuing authority" htmlFor="i9-authority" required>
                      <input id="i9-authority" required value={i9.issuing_authority} onChange={(e) => setI9({ ...i9, issuing_authority: e.target.value })} placeholder="e.g. U.S. Department of State" {...inputProps("i9-issuing-authority-input")} />
                    </Field>
                    <Field label="Document number" htmlFor="i9-number" required>
                      <input id="i9-number" required value={i9.document_number} onChange={(e) => setI9({ ...i9, document_number: e.target.value })} {...inputProps("i9-document-number-input")} />
                    </Field>
                    <Field label="Expiration date (if any)" htmlFor="i9-exp">
                      <input id="i9-exp" type="date" value={i9.expiration_date} onChange={(e) => setI9({ ...i9, expiration_date: e.target.value })} {...inputProps("i9-expiration-input")} />
                    </Field>
                    <Field label="Employee's first day of employment" htmlFor="i9-firstday" required>
                      <input id="i9-firstday" type="date" required value={i9.first_day} onChange={(e) => setI9({ ...i9, first_day: e.target.value })} {...inputProps("i9-first-day-input")} />
                    </Field>
                    <Field label="Employer signature (type full name)" htmlFor="i9-sig" required>
                      <input id="i9-sig" required value={i9.employer_signature} onChange={(e) => setI9({ ...i9, employer_signature: e.target.value })} className="field-input font-serif !text-lg italic" data-testid="i9-employer-signature-input" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Notes" htmlFor="i9-notes">
                        <textarea id="i9-notes" rows={2} value={i9.notes} onChange={(e) => setI9({ ...i9, notes: e.target.value })} {...inputProps("i9-notes-textarea")} />
                      </Field>
                    </div>
                    <button type="submit" disabled={i9Saving} data-testid="i9-section2-submit" className="btn-pink disabled:opacity-60 sm:col-span-2">
                      {i9Saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                      Save Employer Section 2
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Section title="Private Admin Notes">
            <p className="mb-4 text-xs text-charcoal-faint">Only visible to administrators — never shown to the applicant.</p>
            {(app.notes || []).length === 0 ? (
              <p className="text-sm text-charcoal-faint">No notes yet.</p>
            ) : (
              (app.notes || []).slice().reverse().map((n) => (
                <div key={n.id} data-testid={`admin-note-${n.id}`} className="border-b border-blush-100 py-3 last:border-none">
                  <p className="text-sm text-charcoal-light">{n.text}</p>
                  <p className="mt-1 text-xs text-charcoal-faint">{n.by} · {new Date(n.at).toLocaleString("en-US")}</p>
                </div>
              ))
            )}
            <div className="mt-4 flex items-start gap-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Add a private note about this applicant..."
                data-testid="admin-note-input"
                className="field-input flex-1"
              />
              <button onClick={addNote} disabled={noteSaving || !note.trim()} data-testid="admin-note-add"
                className="btn-pink shrink-0 !px-5 !py-3 !text-xs disabled:opacity-50">
                {noteSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Note"}
              </button>
            </div>
          </Section>
        </div>

        <div className="mt-6">
          <Section title="Audit Log (this applicant)">
            {audit.length === 0 ? <p className="text-sm text-charcoal-faint">No recorded activity yet.</p> :
              audit.slice(0, 20).map((l) => (
                <p key={l.id} className="border-b border-blush-100 py-2 text-xs text-charcoal-muted last:border-none">
                  <span className="font-semibold text-charcoal">{l.action.replace(/_/g, " ")}</span> by {l.actor_email} · {new Date(l.at).toLocaleString("en-US")}
                  {l.meta?.filename ? ` · ${l.meta.filename}` : ""}{l.meta?.status ? ` · ${l.meta.status}` : ""}
                </p>
              ))}
          </Section>
        </div>
      </main>
    </div>
  );
};

export default AdminApplicant;
